import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface WalletBalance {
  balance: number;
}

export interface WalletTransaction {
  id: number;
  amount: string | number;
  subtype: string | null;
  description: string | null;
  createdAt: string;
}

export interface WalletTransactionsQuery {
  page: number;
  limit: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface WalletTransactionsResult {
  transactions: WalletTransaction[];
  total: number;
}

export interface ManualTransactionDto {
  amount: number;
  subtype: string;
  description: string;
}

/**
 * Balance + transactions + manual credit/debit for a subject user -
 * extracted from company's driver-wallet-tab (`CompanyPaymentService`).
 * Per-company balance breakdown and the `orderId` transaction column were
 * dropped - taxi-fleet-specific (multi-company driver wallets, order
 * references), no executor/client equivalent, per explicit user
 * instruction to strip taxi-specific functions.
 */
export interface UserWalletConfig {
  /**
   * `companyId` is the subject's OWN current company (forwarded from
   * UserWalletTabComponent's own `@Input() companyId`, same as the
   * original driver-wallet-tab) - some backends (company's
   * CompanyPaymentService) silently fall back to the ACTING ADMIN's own
   * companyId if this is omitted, which is a real, previously-fixed bug
   * (a fleet driver's credit landing in the main company's wallet
   * instead). Pass it through whenever the subject's company is known;
   * backends with no such ambiguity (a self-contained executor wallet)
   * can just ignore the param.
   */
  getBalance: (subjectId: number, companyId?: number) => Observable<ApiResponse<WalletBalance>>;
  getTransactions: (subjectId: number, query: WalletTransactionsQuery, companyId?: number) => Observable<ApiResponse<WalletTransactionsResult>>;
  /** Admin-only (acting on someone ELSE's wallet) - omit for a self-service config; UserWalletTabComponent's [allowManualAdjustments]="false" hides the buttons that would call these. */
  manualCredit?: (subjectId: number, dto: ManualTransactionDto, companyId?: number) => Observable<ApiResponse<unknown>>;
  manualDebit?: (subjectId: number, dto: ManualTransactionDto, companyId?: number) => Observable<ApiResponse<unknown>>;
  /** Currency code for Intl.NumberFormat, e.g. 'UAH'. Defaults to 'UAH' if omitted. */
  currency?: string;
}

export const USER_WALLET_CONFIG = new InjectionToken<UserWalletConfig>('USER_WALLET_CONFIG');
